import React, { useState, useEffect, memo } from 'react';
import styled from 'styled-components';
import { isEqual } from 'lodash';
import Modal from '../../Modal/Modal';

// Components
import ButtonGroup from '../../ButtonGroup/ButtonGroup';
import PrimePresets from './PrimePresets';
import UserPresets from './UserPresets';
import AddPreset from './AddPreset';
import UpdatePreset from './UpdatePreset';
import DeletePreset from './DeletePreset';
import DeletePresetSuccess from './DeletePresetSuccess';
import Loading from '../../Loading/Loading'

import { 
  getGridPresets, 
  createGridPreset, 
  updateGridPreset,
  deleteGridPreset 
} from '../../../api/grids';

const Presets = (props) => {
  const [activeState, setActiveState] = useState("user");
  const [currentPresetState, setCurrentPresetState] = useState(null);
  const [primePresets, setPrimePresets] = useState([]);
  const [userPresets, setUserPresets] = useState([]);
  const [disableSave, setDisableSave] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ });
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    if(props.gridName) {
      setLoading(true);
      getCurrentState();
      getGridPresets(props.gridName).then(presets => {
        setLoading(false);
        if(presets) {
          let foundDefault = presets.findIndex(preset => preset.is_default);
          if(foundDefault !== -1) {
            onLoad(presets[foundDefault])
          } 
          setUserPresets(presets);
          findActiveState();
        }
      })
    }
  }, [props.gridName])

  useEffect(() => {
    findActiveState()
  }, [userPresets.length])


  useEffect(() => {
    if(props && props.api) {
      getCurrentState();

      const eventTypes = [
        'columnVisible',
        'columnPinned	',
        'columnResized',
        'columnMoved',
        'columnRowGroupChanged',
        'displayedColumnsChanged',
        'sortChanged',
        'filterChanged'
      ]
      eventTypes.forEach(eventType => {
        props.api.addEventListener(eventType, () => {
          getCurrentState();
        })
      });
    }
  }, [props.api]);

  useEffect(() => {
    findActiveState();
  }, [currentPresetState]);

  const savePreset = (name) => {
    let newPreset = {
      name,
      grid_ident: props.gridName,
      column_state: props.api.columnController.getColumnState(),
      filter_state: props.api.getFilterModel(),
      sort_state: props.api.getSortModel(),
      is_default: false,
      for_user: true
    }

    createGridPreset(newPreset).then(res => {
      setUserPresets([...userPresets, res]);
      setOpenModal(false);
    })
  }

  const updatePreset = (req) => {
    updateGridPreset(req).then((res) => {
      let tempUsers = [...userPresets];
      let foundIndex = tempUsers.findIndex(user => user.id === res.id);

      if(foundIndex !== -1) {
        tempUsers.splice(foundIndex, 1, req)
      } 
      setUserPresets(tempUsers);
      setOpenModal(false);

    })
  }

  const findActiveState = () => {
    if(userPresets.length > 0) {
      let tempUserPresets = [];
      tempUserPresets = userPresets.map(preset => {
        let presetConfig = {
          column_state: preset.column_state,
          filter_state: preset.filter_state,
          sort_state: preset.sort_state
        }
        
        if(isEqual(presetConfig, currentPresetState)) {
          return { ...preset, is_active: true }
        } else {
          return { ...preset, is_active: false }
        }
      });

      setUserPresets(tempUserPresets);
    }
  }

  const deletePreset = (deletedPreset) => {
    deleteGridPreset(deletedPreset).then(() => {
      let foundIndex = userPresets.findIndex(preset => preset.id === deletedPreset.id);
      if(foundIndex !== -1) {
        let tempPresets = [...userPresets];
        tempPresets.splice(foundIndex, 1);
        setUserPresets(tempPresets);
        onOpenModal("delete-preset-success", deletedPreset)
      }
    })
  }

  const getCurrentState = () => {
    if(props.api) {
      let currentState = {
        column_state: props.api.columnController.getColumnState(),
        filter_state: props.api.getFilterModel(),
        sort_state: props.api.getSortModel()
      }
      setCurrentPresetState(currentState);
      findActiveState()
    }
  }

  const setDefaultPreset = (index) => {
    let tempUserPresets = userPresets.map((preset, i) => {
      if(i === index) {
        let updatedPreset = { 
          ...preset, 
          is_default: true 
        }
        updateGridPreset(updatedPreset);
        return updatedPreset
      } else {
        let updatedPreset = { ...preset, is_default: false }
        updateGridPreset(updatedPreset);
        return updatedPreset;
      }
    });

    setUserPresets(tempUserPresets);
  }

  const onLoad = (preset) => {
    if(preset.column_state) props.api.columnController.applyColumnState({ state: preset.column_state });
    if(preset.sort_state) props.api.setSortModel(preset.sort_state);
    if(preset.filter_state) props.api.setFilterModel(preset.filter_state);
    setDisableSave(true);
  }

  const onResetPreset = () => {
    props.api.setSortModel(null)
    props.api.setFilterModel(null)
    props.api.columnController.columnApi.resetColumnState()
    props.api.columnController.columnApi.resetColumnGroupState()
    props.api.onFilterChanged();
  }

  const headerBtns = [
    {
      title: "Your Presets",
      onClick: () => setActiveState("user")
    },
  ]

  const onOpenModal = (variant, data) => {
    switch(variant){
      case "add-preset":
        setModalConfig({
          header: "Save Preset",
          children: <AddPreset setOpenModal={setOpenModal} data={data} presets={[...primePresets, ...userPresets]} savePreset={savePreset}/>
        })
        setOpenModal(true);
        break;
      case "update-preset":
        setModalConfig({
          header: "Update Preset",
          children: <UpdatePreset setOpenModal={setOpenModal} data={data} presets={[...primePresets, ...userPresets]} updatePreset={updatePreset}/>
        })
        setOpenModal(true);
        break;
      case "delete-preset":
        setModalConfig({
          header: `Delete ${data.name}`,
          subheader: `Are you sure you want to delete ${data.name}?`,
          children: <DeletePreset onOpenModal={onOpenModal} deletePreset={deletePreset} setOpenModal={setOpenModal} data={data}/>
        })
        setOpenModal(true);
        break;
      case "delete-preset-success":
        setModalConfig({
          label: "Preset Deleted",
          header: `Successfully deleted ${data.name}`,
          children: <DeletePresetSuccess setOpenModal={setOpenModal}/>
        })
        setOpenModal(true);
        break;
      default: return;
    }
  }

  if(isLoading) {
    return <Loading />
  }

  return (
    <Container>
      <HeaderActions>
        <ButtonGroup configs={headerBtns}/>
      </HeaderActions>
      <PresetContainer>
        {/* {activeState === "client" && 
          <PrimePresets 
            agGridApi={props.api}
            setDefaultPreset={setDefaultPreset}
            onLoad={onLoad}
            presets={primePresets}/>
        } */}
        {activeState === "user" && 
          <UserPresets 
            agGridApi={props.api}
            onResetPreset={onResetPreset}
            setDefaultPreset={setDefaultPreset}
            onLoad={onLoad}
            updatePreset={updatePreset}
            presets={userPresets}
            disableSave={disableSave}
            onOpenModal={onOpenModal}/>
        }
      </PresetContainer>
      <Modal 
        modalHeading={modalConfig.header}
        modalLabel={modalConfig.label}
        modalSubheading={modalConfig.subheader}
        setOpenModal={setOpenModal}
        size="sm"
        open={openModal}>
          {modalConfig.children}
      </Modal>
    </Container>
  );
};

export default memo(Presets);


const Container = styled.div`
  position: relative;
  width: 260px;
  height: 100%;
`

const HeaderActions = styled.div`
  height: 60px;
  width: 100%;
  position: sticky;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 2.5%;
`

const PresetContainer = styled.div`
  height: calc(100% - 60px);
  width: 100%;
  // overflow-y: scroll;
`